// AssoTresorerie.jsx — Page /dashboard/tresorerie
// Onglets : Vue d'ensemble | Opérations | Import bancaire | Rapprochement | Catégories
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { usePermissions } from '@/lib/usePermissions';
import { downloadAuthed } from '@/lib/media';
import { toast } from 'sonner';
import {
  Coins, TrendingUp, TrendingDown, Wallet, Upload, GitMerge, FolderTree,
  Plus, Trash2, Edit2, Loader2, Lock, Download, Eye, X, Check, Search, Filter, CheckCircle2,
  CreditCard, Ticket, FileText,
} from 'lucide-react';

const TABS = [
  { id: 'overview',  label: 'Vue d\'ensemble', icon: Wallet,    perm: 'view' },
  { id: 'ops',       label: 'Opérations',     icon: Coins,     perm: 'view' },
  { id: 'import',    label: 'Import bancaire',icon: Upload,    perm: 'import_csv' },
  { id: 'rapp',      label: 'Rapprochement',  icon: GitMerge,  perm: 'rapprochement' },
  { id: 'cats',      label: 'Catégories',     icon: FolderTree, perm: 'view' },
];

export default function AssoTresorerie() {
  const { can, loading: permsLoading } = usePermissions();
  const [tab, setTab] = useState('overview');

  if (permsLoading) return <div className="p-8" />;

  if (!can('compta', 'view')) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="compta-denied">
        <div className="card text-center py-12">
          <Lock className="w-8 h-8 mx-auto mb-3 text-[#CBD5E1]" />
          <h3 className="font-display text-lg font-bold text-[#0F172A] mb-2">Accès refusé</h3>
          <p className="text-sm text-[#475569]">La trésorerie est réservée au président, vice-président, secrétaire et trésorier.</p>
        </div>
      </div>
    );
  }

  const tabs = TABS.filter((t) => can('compta', t.perm));
  const canWrite = can('compta', 'write');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="tresorerie-page">
      <div className="mb-6 slide-up">
        <div className="label-uppercase mb-2" style={{ color: 'var(--theme-primary)' }}>Trésorerie</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#0F172A]">
          Comptabilité
        </h1>
        {!canWrite && (
          <p className="text-xs text-amber-600 mt-2 flex items-center gap-1.5">
            <Lock className="w-3 h-3" /> Vous consultez la trésorerie en lecture seule (rôle secrétaire).
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-[#E2E8F0] mb-6 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 overflow-x-auto" data-testid="tresorerie-tabs">
        <div className="flex gap-1 sm:gap-2 min-w-max">
          {tabs.map((t) => {
            const Ic = t.icon;
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  active ? 'text-[#0F172A]' : 'text-[#475569] hover:text-[#0F172A] border-transparent'
                }`}
                style={active ? { borderColor: 'var(--theme-primary)' } : {}}
                data-testid={`tab-${t.id}`}
              >
                <Ic className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'ops' && <OperationsTab canWrite={canWrite} />}
      {tab === 'import' && <ImportTab />}
      {tab === 'rapp' && <RapprochementTab />}
      {tab === 'cats' && <CategoriesTab canWrite={canWrite} />}
    </div>
  );
}

// ──────────────────────────── VUE D'ENSEMBLE ────────────────────────────
function OverviewTab() {
  const [stats, setStats] = useState(null);
  const [enc, setEnc] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/compta/stats').then((r) => setStats(r.data)),
      api.get('/compta/encaissements-en-ligne').then((r) => setEnc(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;
  if (!stats) return null;

  return (
    <div className="space-y-6" data-testid="overview-tab">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard icon={TrendingUp} label="Recettes" value={`${stats.recettes.toFixed(2)} €`} color="#10b981" testid="kpi-recettes" />
        <KpiCard icon={TrendingDown} label="Dépenses" value={`${stats.depenses.toFixed(2)} €`} color="#ef4444" testid="kpi-depenses" />
        <KpiCard
          icon={Wallet}
          label="Trésorerie"
          value={`${stats.tresorerie.toFixed(2)} €`}
          color={stats.tresorerie >= 0 ? '#0ea5e9' : '#dc2626'}
          testid="kpi-tresorerie"
        />
      </div>

      {enc && (enc.total > 0 || enc.nb_recus > 0) && (
        <div className="card" data-testid="encaissements-card" style={{ background: 'linear-gradient(135deg, var(--theme-primary) 0%, color-mix(in srgb, var(--theme-primary) 78%, black) 100%)', color: 'white' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Encaissé en ligne ce mois-ci</div>
              <div className="font-display text-3xl font-black mt-1" data-testid="encaissements-total">{enc.total.toFixed(2)} €</div>
            </div>
            <div className="w-11 h-11 rounded-xl grid place-items-center bg-white/15"><CreditCard className="w-5 h-5" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-white/10 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-90"><Coins className="w-3.5 h-3.5" /> Adhésions</div>
              <div className="font-display font-black text-lg mt-1">{enc.adhesions.total.toFixed(2)} €</div>
              <div className="text-[10px] opacity-70">{enc.adhesions.count} paiement(s)</div>
            </div>
            <div className="rounded-xl bg-white/10 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-90"><Ticket className="w-3.5 h-3.5" /> Billetterie</div>
              <div className="font-display font-black text-lg mt-1">{enc.billetterie.total.toFixed(2)} €</div>
              <div className="text-[10px] opacity-70">{enc.billetterie.count} billet(s)</div>
            </div>
            <div className="rounded-xl bg-white/10 p-3">
              <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-90"><FileText className="w-3.5 h-3.5" /> Reçus émis</div>
              <div className="font-display font-black text-lg mt-1">{enc.nb_recus}</div>
              <div className="text-[10px] opacity-70">ce mois-ci</div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0F172A]">Taux de rapprochement bancaire</h3>
          <span className="text-2xl font-display font-black" style={{ color: 'var(--theme-primary)' }}>{stats.taux_rapprochement}%</span>
        </div>
        <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${stats.taux_rapprochement}%`, background: 'var(--theme-primary)' }} />
        </div>
        <div className="text-xs text-[#475569] mt-2">{stats.nb_rapprochees} / {stats.nb_operations} opérations rapprochées</div>
      </div>

      <div className="card">
        <h3 className="font-bold text-[#0F172A] mb-3">Répartition par catégorie</h3>
        {stats.par_categorie.length === 0 ? (
          <p className="text-sm text-[#475569] py-4 text-center">Aucune opération encore.</p>
        ) : (
          <div className="space-y-2">
            {stats.par_categorie.map((c) => {
              const total = stats.par_categorie.reduce((a, x) => a + Math.abs(x.total), 0);
              const pct = total ? (Math.abs(c.total) / total) * 100 : 0;
              return (
                <div key={c.code} className="flex items-center gap-3 py-1.5" data-testid={`cat-row-${c.code}`}>
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ background: c.couleur }} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-[#0F172A] truncate">{c.label}</span>
                      <span className="font-bold text-[#0F172A] shrink-0 ml-2">{c.total.toFixed(2)} €</span>
                    </div>
                    <div className="h-1 mt-1 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div className="h-full" style={{ width: `${pct}%`, background: c.couleur }} />
                    </div>
                  </div>
                  <span className="text-[10px] text-[#94A3B8] shrink-0 w-12 text-right">{c.count} op.</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon: Ic, label, value, color, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="label-uppercase">{label}</span>
        <div className="w-8 h-8 rounded-lg grid place-items-center" style={{ background: color + '15' }}>
          <Ic className="w-4 h-4" style={{ color }} />
        </div>
      </div>
      <div className="font-display text-2xl sm:text-3xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}

// ──────────────────────────── OPÉRATIONS ────────────────────────────
function OperationsTab({ canWrite }) {
  const [ops, setOps] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [periode, setPeriode] = useState('tout');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [opsR, catsR] = await Promise.all([
        api.get('/compta/operations', { params: { search: search || undefined, type: filterType || undefined } }),
        api.get('/compta/categories'),
      ]);
      setOps(opsR.data); setCats(catsR.data);
    } catch { toast.error('Erreur chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, filterType]);

  const catsById = Object.fromEntries(cats.map((c) => [c.id, c]));

  const exportCsv = () => {
    const token = localStorage.getItem('assoperf_token');
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/compta/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `compta-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
      });
  };

  const computeRange = () => {
    const now = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (periode === 'mois') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
    if (periode === 'trimestre') { const q = Math.floor(now.getMonth() / 3) * 3; return { from: iso(new Date(now.getFullYear(), q, 1)), to: iso(now) }; }
    if (periode === 'annee') return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
    if (periode === 'custom') return { from: dateFrom || undefined, to: dateTo || undefined };
    return {};
  };

  const exportPdf = async () => {
    const { from, to } = computeRange();
    const qs = new URLSearchParams();
    if (from) qs.set('date_from', from);
    if (to) qs.set('date_to', to);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    try {
      await downloadAuthed(`/api/compta/releve/pdf${suffix}`, `releve-compta-${to || new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { toast.error('Export PDF impossible'); }
  };

  return (
    <div data-testid="operations-tab">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <input
            type="text" placeholder="Rechercher (libellé, référence)..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            className="input-base pl-9" data-testid="ops-search"
          />
        </div>
        <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input-base sm:w-44" data-testid="ops-filter-type">
          <option value="">Tous types</option>
          <option value="recette">Recettes</option>
          <option value="depense">Dépenses</option>
        </select>
        <button onClick={exportCsv} className="btn-secondary flex items-center gap-1.5" data-testid="ops-export"><Download className="w-4 h-4" /> CSV</button>
        <select value={periode} onChange={(e) => setPeriode(e.target.value)} className="input-base sm:w-44" data-testid="ops-periode" title="Période du relevé PDF">
          <option value="tout">Relevé : tout</option>
          <option value="mois">Relevé : ce mois</option>
          <option value="trimestre">Relevé : ce trimestre</option>
          <option value="annee">Relevé : cette année</option>
          <option value="custom">Relevé : personnalisé</option>
        </select>
        <button onClick={exportPdf} className="btn-secondary flex items-center gap-1.5" data-testid="ops-export-pdf"><FileText className="w-4 h-4" /> PDF</button>
        {canWrite && (
          <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-1.5" data-testid="ops-add"><Plus className="w-4 h-4" /> Nouvelle opération</button>
        )}
      </div>

      {periode === 'custom' && (
        <div className="flex flex-wrap items-end gap-3 mb-4 -mt-1" data-testid="ops-periode-custom">
          <div>
            <label className="label-uppercase block mb-1">Du</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="input-base" data-testid="ops-date-from" />
          </div>
          <div>
            <label className="label-uppercase block mb-1">Au</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="input-base" data-testid="ops-date-to" />
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>
      ) : ops.length === 0 ? (
        <div className="card text-center py-12 text-sm text-[#475569]" data-testid="ops-empty">Aucune opération.</div>
      ) : (
        <div className="card !p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F9F8]">
              <tr className="text-xs text-[#475569] uppercase tracking-wider">
                <th className="text-left px-3 py-2.5 font-bold">Date</th>
                <th className="text-left px-3 py-2.5 font-bold">Libellé</th>
                <th className="text-left px-3 py-2.5 font-bold hidden sm:table-cell">Catégorie</th>
                <th className="text-right px-3 py-2.5 font-bold">Montant</th>
                <th className="text-center px-3 py-2.5 font-bold hidden md:table-cell">Source</th>
                <th className="text-center px-3 py-2.5 font-bold">Rapp.</th>
                {canWrite && <th className="px-2 py-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {ops.map((o) => {
                const cat = catsById[o.categorie_id];
                return (
                  <tr key={o.id} className="border-t border-[#F1F5F9] hover:bg-[#F9F9F8]" data-testid={`op-row-${o.id}`}>
                    <td className="px-3 py-2.5 text-[#0F172A] whitespace-nowrap">{new Date(o.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-3 py-2.5">
                      <div className="font-medium text-[#0F172A] truncate max-w-xs">{o.libelle}</div>
                      {cat && <div className="text-[10px] text-[#94A3B8] sm:hidden">{cat.label}</div>}
                    </td>
                    <td className="px-3 py-2.5 hidden sm:table-cell">
                      {cat ? (
                        <span className="inline-flex items-center gap-1.5 text-xs">
                          <span className="w-2 h-2 rounded-full" style={{ background: cat.couleur }} />
                          {cat.label}
                        </span>
                      ) : <span className="text-xs text-[#94A3B8]">—</span>}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-bold whitespace-nowrap ${o.type === 'recette' ? 'text-emerald-600' : 'text-rose-600'}`}>
                      {o.type === 'recette' ? '+' : '−'}{o.montant.toFixed(2)} €
                    </td>
                    <td className="px-3 py-2.5 text-center hidden md:table-cell">
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${o.source === 'csv' ? 'bg-blue-50 text-blue-700' : (o.source === 'auto' ? 'bg-emerald-50 text-emerald-700' : 'bg-[#F1F5F9] text-[#0F172A]')}`}>
                        {o.source === 'csv' ? 'Banque' : (o.source === 'auto' ? 'Auto' : 'Manuel')}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      {o.rapproche ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <span className="text-[#CBD5E1]">—</span>}
                    </td>
                    {canWrite && (
                      <td className="px-2 py-2.5">
                        <button
                          onClick={async () => {
                            if (!window.confirm('Supprimer cette opération ?')) return;
                            await api.delete(`/compta/operations/${o.id}`);
                            toast.success('Supprimée');
                            load();
                          }}
                          className="text-[#94A3B8] hover:text-rose-600 p-1"
                          data-testid={`op-delete-${o.id}`}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <OperationModal cats={cats} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />
      )}
    </div>
  );
}

function OperationModal({ cats, onClose, onSaved }) {
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    libelle: '', montant: '', type: 'depense',
    categorie_id: '', mode_paiement: 'cb', reference: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/compta/operations', { ...form, montant: parseFloat(form.montant) });
      toast.success('Opération créée');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  const catsFiltered = cats.filter((c) => c.type === form.type);

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-lg w-full my-6" onClick={(e) => e.stopPropagation()} data-testid="op-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-[#0F172A]">Nouvelle opération</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8] hover:text-[#0F172A]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setForm({ ...form, type: 'recette', categorie_id: '' })} className={`p-3 rounded-lg border-2 ${form.type === 'recette' ? 'border-emerald-500 bg-emerald-50' : 'border-[#E2E8F0]'}`} data-testid="op-type-recette">
              <TrendingUp className="w-5 h-5 mx-auto mb-1 text-emerald-500" />
              <div className="text-xs font-bold">Recette</div>
            </button>
            <button type="button" onClick={() => setForm({ ...form, type: 'depense', categorie_id: '' })} className={`p-3 rounded-lg border-2 ${form.type === 'depense' ? 'border-rose-500 bg-rose-50' : 'border-[#E2E8F0]'}`} data-testid="op-type-depense">
              <TrendingDown className="w-5 h-5 mx-auto mb-1 text-rose-500" />
              <div className="text-xs font-bold">Dépense</div>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Date *</label>
              <input required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="input-base" data-testid="op-date" />
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Montant (€) *</label>
              <input required type="number" step="0.01" min="0" value={form.montant} onChange={(e) => setForm({ ...form, montant: e.target.value })} className="input-base" data-testid="op-montant" placeholder="0.00" />
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Libellé *</label>
            <input required type="text" value={form.libelle} onChange={(e) => setForm({ ...form, libelle: e.target.value })} className="input-base" placeholder="Ex: Boissons réunion juillet" data-testid="op-libelle" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-uppercase block mb-1.5">Catégorie</label>
              <select value={form.categorie_id} onChange={(e) => setForm({ ...form, categorie_id: e.target.value })} className="input-base" data-testid="op-categorie">
                <option value="">— Sans —</option>
                {catsFiltered.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label-uppercase block mb-1.5">Mode paiement</label>
              <select value={form.mode_paiement} onChange={(e) => setForm({ ...form, mode_paiement: e.target.value })} className="input-base" data-testid="op-mode">
                <option value="cb">Carte bancaire</option>
                <option value="virement">Virement</option>
                <option value="cheque">Chèque</option>
                <option value="especes">Espèces</option>
                <option value="stripe">Stripe</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Référence (optionnel)</label>
            <input type="text" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="input-base" placeholder="N° facture, N° chèque..." data-testid="op-reference" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-base" data-testid="op-notes" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5 pt-4 border-t border-[#F1F5F9]">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="op-submit">{saving ? 'Enregistrement…' : 'Créer'}</button>
        </div>
      </form>
    </div>
  );
}

// ──────────────────────────── IMPORT BANCAIRE ────────────────────────────
function ImportTab() {
  const [csv, setCsv] = useState('');
  const [banque, setBanque] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!csv.trim()) return toast.error('Collez votre CSV');
    setSubmitting(true);
    try {
      const { data } = await api.post('/compta/import-bancaire', { csv, banque: banque || undefined });
      setResult(data);
      toast.success(`${data.created} opération(s) importée(s)`);
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur import'); }
    finally { setSubmitting(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsv(ev.target.result);
    reader.readAsText(file, 'utf-8');
  };

  return (
    <div className="space-y-4" data-testid="import-tab">
      <div className="card !bg-blue-50/50 !border-blue-200">
        <h3 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
          <Upload className="w-4 h-4 text-blue-600" /> Importer un relevé bancaire
        </h3>
        <p className="text-sm text-[#0F172A]">
          Exportez le relevé CSV depuis votre espace bancaire en ligne, puis collez-le ici. AssoPerf détecte automatiquement le format (Crédit Agricole, Banque Postale, Banque Populaire, BNP, Société Générale, ou générique).
        </p>
      </div>

      <form onSubmit={submit} className="card">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label-uppercase block mb-1.5">Banque (auto-détection)</label>
            <select value={banque} onChange={(e) => setBanque(e.target.value)} className="input-base" data-testid="import-banque">
              <option value="">Auto-détection</option>
              <option value="credit-agricole">Crédit Agricole</option>
              <option value="banque-postale">La Banque Postale</option>
              <option value="banque-pop">Banque Populaire</option>
              <option value="societe-generale">Société Générale</option>
              <option value="bnp">BNP Paribas</option>
            </select>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Ou charger un fichier .csv</label>
            <input type="file" accept=".csv,text/csv" onChange={onFile} className="input-base" data-testid="import-file" />
          </div>
        </div>
        <label className="label-uppercase block mb-1.5">Contenu CSV</label>
        <textarea
          value={csv} onChange={(e) => setCsv(e.target.value)}
          rows={10} placeholder="Date;Libellé;Débit;Crédit;Solde&#10;03/07/2026;PAIEMENT CB TXIKITO;85,50;0;..."
          className="input-base font-mono text-xs" data-testid="import-csv"
        />
        <div className="flex justify-end mt-4">
          <button type="submit" disabled={submitting} className="btn-primary flex items-center gap-1.5" data-testid="import-submit">
            <Upload className="w-4 h-4" /> {submitting ? 'Import…' : 'Importer'}
          </button>
        </div>
      </form>

      {result && (
        <div className="card !bg-emerald-50/50 !border-emerald-200" data-testid="import-result">
          <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2"><Check className="w-4 h-4" /> Import terminé</h4>
          <ul className="text-sm text-emerald-800 space-y-1">
            <li>• <strong>{result.created}</strong> opération(s) ajoutée(s)</li>
            <li>• <strong>{result.duplicates}</strong> doublon(s) ignoré(s)</li>
            <li>• Banque détectée : <strong>{result.banque_detectee}</strong></li>
          </ul>
          <p className="text-xs text-emerald-700 mt-3">Allez dans l'onglet <strong>Rapprochement</strong> pour faire correspondre ces opérations bancaires avec vos saisies manuelles.</p>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── RAPPROCHEMENT ────────────────────────────
function RapprochementTab() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [stats, setStats] = useState(null);

  const loadStats = () => api.get('/compta/stats').then((r) => setStats(r.data));
  useEffect(() => { loadStats(); }, []);

  const launch = async () => {
    setRunning(true); setResult(null);
    try {
      const { data } = await api.post('/compta/rapprochement-auto', {});
      setResult(data);
      toast.success(`${data.matches} rapprochement(s) effectué(s)`);
      loadStats();
    } catch { toast.error('Erreur'); }
    finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="rapp-tab">
      <div className="card !bg-violet-50/50 !border-violet-200">
        <h3 className="font-bold text-[#0F172A] mb-2 flex items-center gap-2">
          <GitMerge className="w-4 h-4 text-violet-600" /> Rapprochement bancaire automatique
        </h3>
        <p className="text-sm text-[#0F172A]">
          L'algorithme cherche les correspondances entre vos opérations <strong>importées du relevé bancaire</strong> et les <strong>saisies manuelles</strong>, par montant exact + date à ±3 jours + similarité du libellé.
        </p>
      </div>

      {stats && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-bold text-[#0F172A]">État actuel</h4>
            <span className="text-2xl font-display font-black" style={{ color: 'var(--theme-primary)' }}>{stats.taux_rapprochement}%</span>
          </div>
          <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
            <div className="h-full transition-all" style={{ width: `${stats.taux_rapprochement}%`, background: 'var(--theme-primary)' }} />
          </div>
          <div className="text-xs text-[#475569] mt-2">{stats.nb_rapprochees} / {stats.nb_operations} opérations rapprochées</div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={launch} disabled={running} className="btn-primary flex items-center gap-1.5" data-testid="rapp-launch">
          {running ? <><Loader2 className="w-4 h-4 animate-spin" /> En cours…</> : <><GitMerge className="w-4 h-4" /> Lancer le rapprochement</>}
        </button>
      </div>

      {result && (
        <div className="card !bg-emerald-50/50 !border-emerald-200" data-testid="rapp-result">
          <h4 className="font-bold text-emerald-900 mb-2 flex items-center gap-2"><Check className="w-4 h-4" /> Résultat</h4>
          <ul className="text-sm text-emerald-800 space-y-1">
            <li>• <strong>{result.matches}</strong> rapprochement(s) effectué(s)</li>
            <li>• <strong>{result.csv_traitees}</strong> opération(s) bancaire(s) analysée(s)</li>
            <li>• <strong>{result.manuels_restants}</strong> saisie(s) manuelle(s) non rapprochée(s) restante(s)</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// ──────────────────────────── CATÉGORIES ────────────────────────────
function CategoriesTab({ canWrite }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = () => api.get('/compta/categories').then((r) => setCats(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[#94A3B8]" /></div>;

  const recettes = cats.filter((c) => c.type === 'recette');
  const depenses = cats.filter((c) => c.type === 'depense');

  return (
    <div className="space-y-4" data-testid="cats-tab">
      {canWrite && (
        <div className="flex justify-end">
          <button onClick={() => setShowAdd(true)} className="btn-primary flex items-center gap-1.5" data-testid="cat-add"><Plus className="w-4 h-4" /> Nouvelle catégorie</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <CategoryList title="Recettes" cats={recettes} canWrite={canWrite} onChanged={load} colorAccent="#10b981" />
        <CategoryList title="Dépenses" cats={depenses} canWrite={canWrite} onChanged={load} colorAccent="#ef4444" />
      </div>

      {showAdd && <CategoryModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function CategoryList({ title, cats, canWrite, onChanged, colorAccent }) {
  return (
    <div className="card !p-0 overflow-hidden">
      <div className="px-4 py-2.5 bg-[#F9F9F8] border-b border-[#F1F5F9] flex items-center gap-2">
        <span className="w-2 h-2 rounded-full" style={{ background: colorAccent }} />
        <span className="font-bold text-sm text-[#0F172A]">{title}</span>
        <span className="ml-auto text-xs text-[#94A3B8]">{cats.length}</span>
      </div>
      <div>
        {cats.map((c) => (
          <div key={c.id} className="flex items-center gap-2.5 px-4 py-2 border-b border-slate-50 last:border-0" data-testid={`cat-item-${c.code}`}>
            <span className="w-3 h-3 rounded-full shrink-0" style={{ background: c.couleur }} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-[#0F172A] truncate">{c.label}</div>
              <div className="text-[10px] text-[#94A3B8] font-mono">{c.code}</div>
            </div>
            {canWrite && c.custom && (
              <button
                onClick={async () => {
                  if (!window.confirm(`Supprimer "${c.label}" ?`)) return;
                  try {
                    await api.delete(`/compta/categories/${c.id}`);
                    toast.success('Supprimée'); onChanged();
                  } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
                }}
                className="text-[#CBD5E1] hover:text-rose-600 p-1"
                data-testid={`cat-delete-${c.code}`}
              ><Trash2 className="w-3.5 h-3.5" /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ label: '', type: 'depense', couleur: '#64748b' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/compta/categories', form);
      toast.success('Catégorie créée');
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.error || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F172A]/60 backdrop-blur-sm grid place-items-center p-3 fade-in" onClick={onClose}>
      <form onSubmit={submit} className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="cat-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-[#0F172A]">Nouvelle catégorie</h3>
          <button type="button" onClick={onClose} className="text-[#94A3B8]"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-uppercase block mb-1.5">Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, type: 'recette' })} className={`p-2 rounded-lg border-2 text-xs font-bold ${form.type === 'recette' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-[#E2E8F0]'}`}>Recette</button>
              <button type="button" onClick={() => setForm({ ...form, type: 'depense' })} className={`p-2 rounded-lg border-2 text-xs font-bold ${form.type === 'depense' ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-[#E2E8F0]'}`}>Dépense</button>
            </div>
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Libellé *</label>
            <input required type="text" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} className="input-base" data-testid="cat-label" />
          </div>
          <div>
            <label className="label-uppercase block mb-1.5">Couleur</label>
            <input type="color" value={form.couleur} onChange={(e) => setForm({ ...form, couleur: e.target.value })} className="input-base h-10" data-testid="cat-couleur" />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-5">
          <button type="button" onClick={onClose} className="btn-secondary">Annuler</button>
          <button type="submit" disabled={saving} className="btn-primary" data-testid="cat-submit">{saving ? 'Enregistrement…' : 'Créer'}</button>
        </div>
      </form>
    </div>
  );
}
